import './OrderNewTest.scss';
import './OrderWizard.scss';

import { useContext, useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { FormPanel } from '@components/FormPanel/FormPanel';
import { ROUTES } from '@constants/routes';
import { PayorType } from '@customTypes/constants';
import { fetchUserInfo, UserInfo } from '@store/actions';
import { fetchRerequisitionEligibility } from '@store/actions/patientPortal';
import { Context } from '@store/store';

import { OrderSummaryAside } from './components/OrderSummaryAside';
import Stepper from './components/Stepper';
import { useOrderContext } from './hooks/useOrderContext';
import { OrderActions } from './types/Order.actions';
import type { SubjectPayload } from './types/Order.types';
import { clearWizardState } from './utils/orderStorage';

type EligibilityStatus = 'loading' | 'ready' | 'ineligible' | 'error';

function subjectFromProfile(profile: UserInfo): SubjectPayload {
  const contact = profile.contact_info?.[0];

  return {
    name: {
      first: profile.name?.first,
      middle: profile.name?.middle,
      last: profile.name?.last,
    },
    contact_info: [
      {
        email: contact?.email,
        phone: contact?.phone,
        address: contact?.address,
      },
    ],
    demographics: {
      dob: profile.demographics?.dob,
      sex: profile.demographics?.sex,
    },
  };
}

export function OrderNewTest() {
  const [appState] = useContext(Context) as [
    {
      hasLoadedUserInfo: boolean;
      userInfo?: UserInfo;
    },
  ];
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    state: { products, selected_product_ids: selectedProductIds },
    dispatch,
    requiresQuestionnaireStep,
    deselectProduct,
    selectProduct,
  } = useOrderContext();
  const individualId = searchParams.get('individual') || undefined;
  const [status, setStatus] = useState<EligibilityStatus>('loading');
  const [requestAttempt, setRequestAttempt] = useState(0);

  useEffect(() => {
    if (!individualId && !appState.hasLoadedUserInfo) return;

    let isActive = true;
    setStatus('loading');

    const profileRequest = individualId ? fetchUserInfo(individualId) : Promise.resolve(appState.userInfo);

    Promise.all([fetchRerequisitionEligibility(individualId), profileRequest])
      .then(([response, profile]) => {
        if (!isActive) return;

        if (!profile) {
          setStatus('error');
          return;
        }

        if (!response.is_eligible || !response.new_order?.products.length) {
          setStatus('ineligible');
          return;
        }

        clearWizardState();
        dispatch({ type: OrderActions.RESET });
        dispatch({
          type: OrderActions.SET_SUBJECT,
          value: subjectFromProfile(profile),
        });
        dispatch({
          type: OrderActions.SET_GUARDIAN,
          value: null,
        });
        dispatch({
          type: OrderActions.SET_IS_REREQUISITION,
          value: true,
        });
        dispatch({
          type: OrderActions.ELIGIBILITY_UPDATED,
          value: {
            is_eligible: true,
            campaign: null,
            products: response.new_order.products,
            pricing: {
              payor_type: PayorType.SELF,
              original_price_cents: response.new_order.price_cents,
              price_cents: response.new_order.price_cents,
              discount_cents: 0,
              discount: null,
              promo_code: null,
            },
          },
        });
        setStatus('ready');
      })
      .catch(() => {
        if (isActive) {
          setStatus('error');
        }
      });

    return () => {
      isActive = false;
    };
  }, [appState.hasLoadedUserInfo, appState.userInfo, dispatch, individualId, requestAttempt]);

  const continueToWizard = () => {
    navigate({
      pathname: `${ROUTES.ORDER}/${ROUTES.ORDER_WIZARD}`,
      search: individualId ? `?individual=${encodeURIComponent(individualId)}` : '',
    });
  };

  if (status === 'loading') {
    return (
      <main className='wizard-page rerequisition-page rerequisition-page--centered' data-testid='order-new-test'>
        <Spinner animation='border' role='status' />
      </main>
    );
  }

  if (status === 'error' || status === 'ineligible') {
    return (
      <main className='wizard-page rerequisition-page' data-testid='order-new-test'>
        <FormPanel maxWidth='md' layout='sectioned'>
          <FormPanel.Header title="We couldn't verify your eligibility" />
          <FormPanel.Body>
            <p className='form-panel__page-subtitle mt-1'>
              There was a problem verifying your eligibility for this program.
            </p>
          </FormPanel.Body>
          <FormPanel.Footer variant='actions'>
            {status === 'error' ? (
              <Button onClick={() => setRequestAttempt(attempt => attempt + 1)}>Try again</Button>
            ) : (
              <Link to={ROUTES.MY_ORDERS} className='btn btn-primary'>
                My Orders
              </Link>
            )}
          </FormPanel.Footer>
        </FormPanel>
      </main>
    );
  }

  return (
    <main className='wizard-page rerequisition-page' data-testid='order-new-test'>
      <FormPanel maxWidth='lg' layout='sectioned' className='wizard'>
        <FormPanel.Header title='Start your next order' />
        <FormPanel.Body className='form-panel__body--flush'>
          <div className='wizard__layout'>
            <div className='wizard__main'>
              <div className='wizard__stepper'>
                <Stepper total={requiresQuestionnaireStep ? 3 : 2} current={0} />
              </div>
              <div className='rerequisition-get-started'>
                <p>
                  You’re eligible for follow-up testing. All available tests have been added to your order, but you can
                  remove any you don’t want. No new sample is required, and results are typically ready within 2 weeks.
                </p>
                <p>
                  Ordering is simple. On the next steps you’ll be asked to provide family history and re-consent to
                  testing.
                </p>
                <div className='wizard__actions'>
                  <Link to={ROUTES.MY_ORDERS} className='btn btn-link'>
                    Cancel
                  </Link>
                  <Button className='primary' onClick={continueToWizard} disabled={!selectedProductIds.length}>
                    Continue
                  </Button>
                </div>
              </div>
            </div>
            <OrderSummaryAside
              products={products || []}
              selectedProductIds={selectedProductIds}
              onDeselectProduct={deselectProduct}
              onSelectProduct={selectProduct}
            />
          </div>
        </FormPanel.Body>
      </FormPanel>
    </main>
  );
}
